"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useFormatter } from "@/hooks/use-formatter"

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  getAppointmentAvailability,
  type AvailabilityReason,
  type AvailabilitySlot,
} from "@/lib/appointments-api";

/**
 * Only a genuine double-book of the same doctor makes a slot unusable — a
 * blockout, a full room or a closed day are judgement calls the desk may
 * override (the backend warns, never blocks, on those). Mirrors the mobile
 * wizard's `HARD_REASONS`. A clinic whose doctors see several patients at
 * once gets `doctor_booked` instead of `busy`, so it lands with the soft ones.
 */
const HARD_REASONS: AvailabilityReason[] = ["busy"];

const EMPTY: AvailabilitySlot[] = [];

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Local wall-clock `HH:mm` of an instant — what the `<input type="time">` holds. */
export function localTime(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface SlotGridProps {
  /** Clinic calendar day, `YYYY-MM-DD`. Nothing is fetched while empty. */
  date: string;
  doctorId?: number | null;
  userId?: number | null;
  appointmentType?: "clinic_visit" | "lead_call";
  durationMinutes: number;
  roomId?: string | null;
  /** Edit mode: the appointment being moved must not block its own time. */
  excludeAppointmentId?: string;
  /** The selected local time, `HH:mm`, or null. */
  value: string | null;
  onSelect: (time: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * 86-01: the desk's slot grid — the same `/appointments/availability` feed the
 * phone uses, drawn as a compact 4-across chip grid under the date/time
 * inputs. The manual time input stays the fallback for anything off-grid.
 */
export function SlotGrid({
  date,
  doctorId,
  userId,
  appointmentType = "clinic_visit",
  durationMinutes,
  roomId,
  excludeAppointmentId,
  value,
  onSelect,
  disabled = false,
  className,
}: SlotGridProps) {
  const t = useTranslations("appointments.sheet.slots");
  const format = useFormatter();
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date);

  const availability = useQuery({
    queryKey: [
      "appointments",
      "availability",
      date,
      doctorId ?? null,
      userId ?? null,
      appointmentType,
      durationMinutes,
      roomId ?? null,
      excludeAppointmentId ?? null,
    ],
    queryFn: () =>
      getAppointmentAvailability({
        date,
        duration_minutes: durationMinutes,
        type: appointmentType,
        ...(userId ? { user_id: userId } : {}),
        ...(doctorId ? { doctor_id: doctorId } : {}),
        ...(roomId ? { room_id: roomId } : {}),
        ...(excludeAppointmentId ? { exclude_appointment_id: excludeAppointmentId } : {}),
      }),
    enabled: validDate,
    staleTime: 30_000,
  });

  const slots = availability.data ?? EMPTY;

  const rows = useMemo(
    () =>
      slots.map((s) => {
        const time = localTime(s.start);
        const hard = s.reasons.some((r) => HARD_REASONS.includes(r));
        const soft = s.reasons.filter((r) => !HARD_REASONS.includes(r));
        return { key: s.start, time, hard, soft };
      }),
    [slots]
  );

  const reasonLabel = (reason: AvailabilityReason): string => {
    switch (reason) {
      case "busy":
        return t("reason.busy");
      case "doctor_booked":
        return t("reason.doctorBooked");
      case "blockout":
        return t("reason.blockout");
      case "over_capacity":
        return t("reason.overCapacity");
      case "outside_hours":
        return t("reason.outsideHours");
      default:
        return reason;
    }
  };

  if (!validDate) return null;

  const dayLabel = format.dateTime(new Date(`${date}T00:00:00`), {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className={cn("flex flex-col gap-2", className)} data-slot="slot-grid">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {t("label")}
        </span>
        <span className="text-[11px] tabular-nums text-text-dim">{dayLabel}</span>
      </div>

      {availability.isLoading ? (
        <div className="grid grid-cols-4 gap-1.5" aria-busy>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      ) : availability.isError ? (
        <p className="text-xs text-destructive">{t("loadFailed")}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("empty")}</p>
      ) : (
        <div role="group" aria-label={t("label")} className="grid grid-cols-4 gap-1.5">
          {rows.map((slot) => {
            const selected = value === slot.time;
            const title = slot.hard
              ? reasonLabel("busy")
              : slot.soft.length > 0
                ? slot.soft.map(reasonLabel).join(" · ")
                : undefined;
            return (
              <button
                key={slot.key}
                type="button"
                disabled={disabled || slot.hard}
                aria-pressed={selected}
                title={title}
                onClick={() => onSelect(slot.time)}
                className={cn(
                  "relative flex h-8 items-center justify-center rounded-[8px] border text-[12.5px] font-medium tabular-nums transition-colors",
                  selected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "bg-muted/40 text-foreground hover:border-primary/50",
                  slot.hard &&
                    "cursor-not-allowed border-border bg-muted/20 text-muted-foreground/60 line-through hover:border-border",
                  disabled && !slot.hard && "opacity-60"
                )}
              >
                {slot.time}
                {!slot.hard && slot.soft.length > 0 && (
                  <span
                    aria-hidden
                    className={cn(
                      "absolute right-1 top-1 size-1.5 rounded-full",
                      selected ? "bg-primary-foreground" : "bg-amber-500"
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {rows.length > 0 && (
        <p className="text-[11px] text-text-dim">{t("legend")}</p>
      )}
    </div>
  );
}
